import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { authFetchJson } from '../lib/authFetch'
import type { Organization } from '../../../api/src/db/schema/crm'

interface OrgRow extends Organization {
  peopleCnt?: number
  projectsCnt?: number
}

interface ListResponse {
  organizations: OrgRow[]
}

export function OrganizationsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [orgs, setOrgs] = useState<OrgRow[]>([])
  const [filteredOrgs, setFilteredOrgs] = useState<OrgRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedType, setSelectedType] = useState<string>('')
  const [types, setTypes] = useState<Set<string>>(new Set())

  const orgTypeLabels: Record<string, string> = {
    university: 'University',
    research_institute: 'Research Institute',
    corporate: 'Corporate',
    startup: 'Startup',
    vc: 'Venture Capital',
    government: 'Government',
    ngo: 'NGO',
    other: 'Other',
  }

  // Load organizations
  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        setIsLoading(true)
        const data = await authFetchJson<ListResponse>('/api/v2/organizations')
        setOrgs(data.organizations || [])

        // Extract unique types
        const typeSet = new Set<string>()
        data.organizations?.forEach((org) => {
          typeSet.add(org.type)
        })
        setTypes(typeSet)

        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load organizations')
        setOrgs([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchOrgs()
  }, [])

  // Apply filters
  useEffect(() => {
    let filtered = orgs

    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (org) =>
          org.name.toLowerCase().includes(q) ||
          org.legal_name?.toLowerCase().includes(q)
      )
    }

    if (selectedType) {
      filtered = filtered.filter((org) => org.type === selectedType)
    }

    setFilteredOrgs(filtered)
  }, [searchQuery, selectedType, orgs])

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border-subtle bg-bg-surface p-6">
        <h1 className="text-h1 text-text-primary mb-2">Organizations</h1>
        <p className="text-body text-text-secondary">
          Universities, institutes, companies, and partners in the network
        </p>
      </div>

      {/* Search & Filters */}
      <div className="border-b border-border-subtle bg-bg-body p-6 space-y-4">
        {/* Search Box */}
        <input
          type="text"
          placeholder="Search by name or legal name…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-3 py-2 bg-bg-surface border border-border-subtle rounded-md text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary-red"
        />

        {/* Type Filter */}
        {types.size > 0 && (
          <div className="flex gap-2 items-center flex-wrap">
            <span className="text-xs text-text-tertiary font-medium">Type:</span>
            {Array.from(types).map((type) => (
              <button
                key={type}
                onClick={() => setSelectedType(selectedType === type ? '' : type)}
                className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                  selectedType === type
                    ? 'bg-primary-red text-white'
                    : 'bg-bg-surface border border-border-subtle text-text-secondary hover:bg-border-subtle'
                }`}
              >
                {orgTypeLabels[type] || type}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-bg-body">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <div className="w-8 h-8 border-2 border-primary-red border-t-transparent rounded-full animate-spin" />
              <span className="text-body text-text-secondary">Loading…</span>
            </div>
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="rounded-lg bg-bg-surface border border-border-subtle p-4">
              <p className="text-body text-red-400">Error: {error}</p>
            </div>
          </div>
        ) : filteredOrgs.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-body text-text-secondary mb-2">
                No organizations found
              </p>
              <p className="text-sm text-text-tertiary">
                Try adjusting your search or filters
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-subtle bg-bg-surface">
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    People
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Projects
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredOrgs.map((org) => (
                  <tr
                    key={org.id}
                    onClick={() => navigate(`/organizations/${org.id}`)}
                    className="border-b border-border-subtle hover:bg-bg-surface cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4">
                      <p className="text-body font-medium text-text-primary">
                        {org.name}
                      </p>
                      {org.legal_name && (
                        <p className="text-sm text-text-secondary">{org.legal_name}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-block px-2.5 py-1 bg-bg-surface border border-border-subtle rounded-full text-xs text-text-secondary">
                        {orgTypeLabels[org.type] || org.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-body text-text-secondary">
                        {org.city && org.country
                          ? `${org.city}, ${org.country}`
                          : org.city || org.country || '—'}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-bg-surface text-text-primary text-xs font-medium">
                        {org.peopleCnt || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-bg-surface text-text-primary text-xs font-medium">
                        {org.projectsCnt || 0}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
